<template>
  <div>
    <template v-if="configRef">
      <slot v-if="configRef.title" name="header-slot">
        <h5>{{ configRef.title }}</h5>
      </slot>
      <template v-if="renderFieldInTabs">
        <v-tabs v-model="tabValue" color="primary" class="mt-4">
          <v-tab
            v-for="(tab, tabIndex) in configRef.tabs"
            :key="tabIndex"
            :value="tabIndex"
            :text="tab.name"
          />
        </v-tabs>
        <v-tabs-window v-model="tabValue">
          <v-tabs-window-item
            v-for="(tab, tabIndex) in configRef.tabs"
            :key="tabIndex"
            :value="tabIndex"
          >
            <FormKit v-model="model" type="form" :actions="false">
              <FormKitSchema
                :schema="schema"
                :data="model"
                :library="library"
              />
              <slot name="default" />
              <slot name="form-submit" />
            </FormKit>
          </v-tabs-window-item>
        </v-tabs-window>
      </template>
      <FormKit
        v-else-if="renderAllFieldsAtOnce"
        v-model="model"
        type="form"
        :actions="false"
      >
        <FormKitSchema :schema="schema" :data="model" :library="library" />
        <slot name="default" />
        <slot name="form-submit" />
      </FormKit>
      <v-empty-state v-else text="Keine Konfiguration nötig"></v-empty-state>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { FormKitSchemaDefinition } from '@formkit/core'
import type { FormKitGroupValue } from '@formkit/core'
import { computed, markRaw, onBeforeMount, reactive, ref, watch } from 'vue'

import { i18n } from '@/lang/setup'

import SchemaBrelagNetmaskField from './editor-fields/formkit/SchemaBrelagNetmaskField.vue'
import SchemaBrelagOfferSelectField from './editor-fields/formkit/SchemaBrelagOfferSelectField.vue'
import SchemaCheckboxField from './editor-fields/formkit/SchemaCheckboxField.vue'
import SchemaColorField from './editor-fields/formkit/SchemaColorField.vue'
import SchemaDateField from './editor-fields/formkit/SchemaDateField.vue'
import SchemaEgateField from './editor-fields/formkit/SchemaEgateField.vue'
import SchemaEquipmentConfigField from './editor-fields/formkit/SchemaEquipmentConfigField.vue'
import SchemaImageCropField from './editor-fields/formkit/SchemaImageCropField.vue'
import SchemaImageUploadField from './editor-fields/formkit/SchemaImageUploadField.vue'
import SchemaInputField from './editor-fields/formkit/SchemaInputField.vue'
import SchemaJsonField from './editor-fields/formkit/SchemaJsonField.vue'
import SchemaLabelField from './editor-fields/formkit/SchemaLabelField.vue'
import SchemaMarkdownField from './editor-fields/formkit/SchemaMarkdownField.vue'
import SchemaMaxFlexField from './editor-fields/formkit/SchemaMaxFlexField.vue'
import SchemaNumberField from './editor-fields/formkit/SchemaNumberField.vue'
import SchemaOrderingField from './editor-fields/formkit/SchemaOrderingField.vue'
import SchemaProfilesSelectField from './editor-fields/formkit/SchemaProfilesSelectField.vue'
import SchemaRelatedModelField from './editor-fields/formkit/SchemaRelatedModelField.vue'
import SchemaRotationField from './editor-fields/formkit/SchemaRotationField.vue'
import SchemaSelectField from './editor-fields/formkit/SchemaSelectField.vue'
import SchemaSwwField from './editor-fields/formkit/SchemaSwwField.vue'
import {
  FormBuilderConfig,
  FormFieldType,
  parseFields,
} from './formBuilderHelper'
import { convertFormBuilderConfigToFormKitSchemaDefinition } from './formUtil'

export type FormBuilderProps = {
  config: FormBuilderConfig
  form: { $valid: boolean; loading: boolean }
}

const tabValue = ref(0)
const emit = defineEmits(['update:modelValue'])
const props = withDefaults(defineProps<FormBuilderProps>(), {
  config: () => {
    return {
      model: {},
    }
  },
})

const schema = computed<FormKitSchemaDefinition>(() => {
  if (!props.config?.tabs?.length && !props.config?.fields) return null

  return convertFormBuilderConfigToFormKitSchemaDefinition(
    props.config.tabs?.length
      ? props.config.tabs.at(tabValue.value).fields
      : props.config.fields,
    props.form
  )
})
const renderAllFieldsAtOnce = computed(() => {
  return configRef.value.fields || configRef.value.tabs?.length === 1
})
const renderFieldInTabs = computed(() => {
  return configRef.value?.tabs?.length > 1
})
const configRef = ref<FormBuilderConfig>(null)
const model = reactive<FormKitGroupValue>({}) // The model

onBeforeMount(loadConfig)

watch(model, () => emit('update:modelValue', model), { deep: true })

function loadConfig() {
  try {
    const newConfig: FormBuilderConfig = JSON.parse(
      JSON.stringify(props.config)
    )

    // Check for custom pre-defined fields
    if (newConfig?.tabs) {
      newConfig.tabs.forEach((tab) => {
        parseFields(tab.fields, props.config?.model, i18n)
      })
    } else if (newConfig?.fields) {
      parseFields(newConfig.fields, props.config?.model, i18n)
    }
    model.value = props.config?.model
    configRef.value = newConfig
  } catch (err) {
    console.log(err)
    model.value = {}
    configRef.value = {
      title: 'Error',
      fields: [
        {
          key: 'error',
          type: FormFieldType.LABEL,
          properties: {
            text: err.toString(),
            label: 'Error Building Form',
          },
        },
      ],
      model: {},
    }
  }
}

defineExpose({
  model,
})

// Add custom component to the library
const library = markRaw({
  SchemaInputField,
  SchemaSelectField,
  SchemaBrelagOfferSelectField,
  SchemaCheckboxField,
  SchemaColorField,
  SchemaNumberField,
  SchemaRotationField,
  SchemaEgateField,
  SchemaBrelagNetmaskField,
  SchemaRelatedModelField,
  SchemaLabelField,
  SchemaSwwField,
  SchemaOrderingField,
  SchemaDateField,
  SchemaEquipmentConfigField,
  SchemaProfilesSelectField,
  SchemaImageUploadField,
  SchemaMaxFlexField,
  SchemaMarkdownField,
  SchemaJsonField,
  SchemaImageCropField,
})
</script>
